import { POPUP_CONFIG } from '@/constants/notification.constants';
import TooltipClose from '@assets/images/tooltip-close.svg';
import { ReactNode, useEffect, useState } from 'react';

type NotificationProps = {
	slideType?: Slide;
	popUpPosition?: PopUpPosition;
	isSticky?: boolean;
	icon?: JSX.Element;
	classIcon?: string;
	bgColor?: string;
	tooltipColor?: string;
	descriptionHtml: ReactNode;
	isCtaButton?: boolean;
	ctaText?: string;
	onCtaClick?: () => void;
	onClose: () => void;
	onDismiss: () => void;
	delayTime?: number;
	displayDuration?: number;
};

export enum Slide {
	UP = 'UP',
	DOWN = 'DOWN',
}

export const slideClass: Record<Slide, string> = {
	[Slide.UP]: 'animate-slideUp',
	[Slide.DOWN]: 'animate-slideDown',
};

export enum PopUpPosition {
	TOP_RIGHT = 'TOP_RIGHT',
	BOTTOM = 'BOTTOM',
}

export const popUpPositionClass: Record<PopUpPosition, string> = {
	[PopUpPosition.TOP_RIGHT]: 'top-[53px] right-0 tablet:right-6 desktop:top-[52px] desktop:right-0',
	[PopUpPosition.BOTTOM]: 'bottom-3 right-0',
};

export const toolTipClass: Record<PopUpPosition, string> = {
	[PopUpPosition.TOP_RIGHT]: '-top-[5px] right-[6px] desktop:right-[196px] border-x-[6px] border-b-[6px] ',
	[PopUpPosition.BOTTOM]: '-bottom-[5px] right-1/2 desktop:right-1/2 border-x-[6px] border-t-[6px] ',
};

export default function PopUp({
	slideType = Slide.UP,
	popUpPosition = PopUpPosition.TOP_RIGHT,
	isSticky = false,
	icon,
	classIcon,
	bgColor = 'bg-blue',
	tooltipColor = 'border-blue',
	descriptionHtml,
	isCtaButton,
	ctaText,
	onCtaClick,
	onClose,
	onDismiss,
	delayTime = 0,
	displayDuration = POPUP_CONFIG.DISPLAY_DURATION,
}: NotificationProps) {
	const [showPopUp, setShowPopUp] = useState(false);

	useEffect(() => {
		const show = setTimeout(() => {
			setShowPopUp(true);
		}, delayTime);
		const dismiss = setTimeout(onClose, delayTime + displayDuration);

		return () => {
			clearTimeout(show);
			clearTimeout(dismiss);
		};
	}, []);
	return (
		<>
			{showPopUp && (
				<div className={`w-full absolute z-40 ${slideClass[slideType]} ${popUpPositionClass[popUpPosition]} ${isSticky && 'sticky'}`}>
					<div className={`desktop:container flex ${popUpPosition === PopUpPosition.BOTTOM ? 'justify-center' : 'justify-end'} relative`}>
						<div className='px-6 tablet:px-0 desktop:px-[110px]'>
							{/* Tooltip */}
							<div
								className={`desktop:hidden h-0 w-0 border-x-transparent ${tooltipColor} absolute ${
									popUpPosition === PopUpPosition.TOP_RIGHT && 'mr-6 tablet:mr-0'
								} ${toolTipClass[popUpPosition]}`}
							/>
							<div className={`${bgColor} w-full tablet:w-[327px] rounded p-2 desktop:p-4`}>
								<div className='flex items-center desktop:items-start'>
									{/* Icon */}
									<div data-testid='icon-div' className={`mb-auto ${classIcon}`}>
										<>{icon}</>
									</div>

									{/* Description */}
									<div className='ml-2'>{descriptionHtml}</div>

									{/* CTA button */}
									{isCtaButton && (
									<button
										className='h-8 w-[57px] bg-white rounded-full text-cyan px-[17px] py-2 font-semibold text-sm ml-2'
										onClick={onCtaClick}>
										{ctaText}
									</button>
									)}

									{/* Close Button */}
									<button className='mb-auto ml-1' data-testid='close-btn' onClick={onDismiss}>
										<TooltipClose />
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
